import { FC, useEffect, useMemo, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { debounce } from 'lodash'

import { Lead } from '@forwardflow/services/LeadService'
import { getStorageKey } from '@forwardflow/helpers/utils'
import { useDummyLeads } from '@forwardflow/hooks/customHooks/conversation/useDummyLeads'
import { useDemoConversationStore } from '@forwardflow/store/demoConversationStore'
import { LeadListItem } from '../../LeadList/LeadListItem'
import { SearchField } from '@forwardflow/ui'
import { DemoLead } from '@forwardflow/types/demo-conversation'

interface DemoLeadListProps {
   onSelectLead: (leadId: number | null, leadData?: DemoLead) => void
   selectedStageFilter: string
}

export const DemoLeadList: FC<DemoLeadListProps> = ({
   onSelectLead,
   selectedStageFilter,
}) => {
   const { id } = useParams()
   const navigate = useNavigate()
   const [selectedLeadId, setSelectedLeadId] = useState<number | null>(
      id ? Number(id) : null
   )

   const [searchInput, setSearchInput] = useState('')
   const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')

   const {
      getLeadConversationData,
      getTimelineHistory,
      leadConversations
   } = useDemoConversationStore()

   const LEADS_LIMIT = 20

   const queryParams = useMemo(
      () => ({
         limit: LEADS_LIMIT,
         page: 1,
         stage:
            selectedStageFilter !== 'all-stages'
               ? selectedStageFilter.toLowerCase()
               : undefined,
         search: debouncedSearchTerm.trim() || undefined,
      }),
      [selectedStageFilter, debouncedSearchTerm]
   )

   const { data: dummyLeads, loading } = useDummyLeads(queryParams)

   const debouncedSetSearch = useCallback(
      debounce((value: string) => {
         setDebouncedSearchTerm(value)
      }, 500),
      []
   )

   const handleSearchChange = useCallback(
      (value: string) => {
         setSearchInput(value)
         debouncedSetSearch(value)
      },
      [debouncedSetSearch]
   )

   useEffect(() => {
      return () => {
         debouncedSetSearch.cancel()
      }
   }, [debouncedSetSearch])

   const filteredLeads = useMemo(() => {
      let leads = dummyLeads || []

      if (debouncedSearchTerm.trim()) {
         const searchTerm = debouncedSearchTerm.toLowerCase().trim()

         leads = leads.filter((lead: DemoLead) => {
            const firstName = lead.userInfo.firstName?.toLowerCase() || ''
            const lastName = lead.userInfo.lastName?.toLowerCase() || ''
            const email = lead.userInfo.email?.toLowerCase() || ''
            const phone = lead.userInfo.phone?.toLowerCase() || ''
            const fullName = `${firstName} ${lastName}`.toLowerCase()

            return (
               firstName.includes(searchTerm) ||
               lastName.includes(searchTerm) ||
               fullName.includes(searchTerm) ||
               email.includes(searchTerm) ||
               phone.includes(searchTerm)
            )
         })
      }

      return leads.sort((a: DemoLead, b: DemoLead) => {
         const getLastActivityTime = (lead: DemoLead) => {
            const leadData = lead.leadId ? getLeadConversationData(lead.leadId) : null
            if (leadData && leadData.timelineHistory && leadData.timelineHistory.length > 0) {
               const lastItem = leadData.timelineHistory[leadData.timelineHistory.length - 1]
               return lastItem.ts || lastItem.timestamp || leadData.lastUpdate || '1970-01-01T00:00:00.000Z'
            }

            if (lead.preDefinedTimelineHistory && lead.preDefinedTimelineHistory.length > 0) {
               const lastPredefinedItem = lead.preDefinedTimelineHistory[lead.preDefinedTimelineHistory.length - 1]
               return lastPredefinedItem.ts || lastPredefinedItem.timestamp || '1970-01-01T00:00:00.000Z'
            }

            return '1970-01-01T00:00:00.000Z'
         }

         const timestampA = getLastActivityTime(a)
         const timestampB = getLastActivityTime(b)

         return new Date(timestampB).getTime() - new Date(timestampA).getTime()
      })
   }, [dummyLeads, debouncedSearchTerm, leadConversations, getTimelineHistory])

   const transformDummyLead = useCallback((dummyLead: DemoLead): Lead => {
      const leadConversationData = dummyLead.leadId !== undefined
         ? getLeadConversationData(dummyLead.leadId)
         : undefined

      let lastConversationContent = ''
      let lastConversationDate = ''

      if (leadConversationData && dummyLead.leadId !== undefined) {
         const timelineHistory = getTimelineHistory(dummyLead.leadId)
         
         if (timelineHistory.length > 0) {
            const lastItem = timelineHistory[timelineHistory.length - 1]
            lastConversationContent = lastItem.content || ''
            lastConversationDate = lastItem.ts || leadConversationData.lastUpdate || lastConversationDate
         } else if (leadConversationData.recentConversation) {
            lastConversationContent = leadConversationData.recentConversation
            lastConversationDate = leadConversationData.lastUpdate || lastConversationDate
         }
      } else {
         if (dummyLead.preDefinedTimelineHistory && dummyLead.preDefinedTimelineHistory.length > 0) {
            const lastPredefinedItem = dummyLead.preDefinedTimelineHistory[dummyLead.preDefinedTimelineHistory.length - 1]
            lastConversationContent = lastPredefinedItem.content || ''
            lastConversationDate = lastPredefinedItem.ts || lastConversationDate
         }
      }

      return {
         id: (dummyLead.leadId ?? '').toString(),
         meta: {
            appointment: {
               date: dummyLead.appointmentInfo?.date || '',
               status: dummyLead.appointmentInfo?.status || 'Scheduled',
               platform: dummyLead.appointmentInfo?.platform || 'Zoom'
            }
         },
         leadId: dummyLead.leadId ?? 0,
         email: dummyLead.userInfo.email,
         phoneNo: dummyLead.userInfo.phone,
         lastName: dummyLead.userInfo.lastName,
         firstName: dummyLead.userInfo.firstName,
         stageInfo: {
            id: dummyLead.stageInfo.id.toString(),
            name: dummyLead.stageInfo.name,
            stageGroup: {
               id: '1',
               name: 'Demo Group',
               type: 'demo'
            }
         },
         lastConversation: {
            date: lastConversationDate,
            type: leadConversationData?.currentCampaignType || '',
            content: lastConversationContent
         },
         unreadMessages: 0
      }
   }, [getLeadConversationData, getTimelineHistory, leadConversations])

   useEffect(() => {
      if (filteredLeads.length === 0 || loading) {
         return
      }

      if (id) {
         const urlLead = filteredLeads.find((lead: DemoLead) => lead.leadId === Number(id))
         if (urlLead) {
            setSelectedLeadId(Number(id))
            onSelectLead(Number(id), urlLead)
            return
         } else {
            console.warn(`Lead ${id} not found in current stage filter, falling back to stored lead`)
         }
      }

      if (!id || (id && !filteredLeads.find((lead: DemoLead) => lead.leadId === Number(id)))) {
         const storageKey = getStorageKey(selectedStageFilter, 'Demo Source')
         const storedLeadId = localStorage.getItem(storageKey)

         if (storedLeadId) {
            const storedLead = filteredLeads.find(
               (lead: DemoLead) => lead.leadId === Number(storedLeadId)
            )
            if (storedLead) {
               const currentUrl = new URL(window.location.href)
               const searchParams = currentUrl.searchParams.toString()
               const urlSuffix = searchParams ? `?${searchParams}` : ''

               navigate(`/demo-conversations/${storedLeadId}${urlSuffix}`, { replace: true })
               return
            } else {
               console.warn(`Stored lead ${storedLeadId} not found in current stage filter`)
            }
         }

         const firstLead = filteredLeads[0]
         if (firstLead) {
            const currentUrl = new URL(window.location.href)
            const searchParams = currentUrl.searchParams.toString()
            const urlSuffix = searchParams ? `?${searchParams}` : ''

            navigate(`/demo-conversations/${firstLead.leadId}${urlSuffix}`, { replace: true })
         } else {
            console.warn('No leads available for current stage filter')
            setSelectedLeadId(null)
            onSelectLead(null)
         }
      }
   }, [filteredLeads, selectedStageFilter, id, navigate, onSelectLead, loading])

   useEffect(() => {
      const currentLeadId = id ? Number(id) : null
      if (currentLeadId !== selectedLeadId) {
         setSelectedLeadId(currentLeadId)
      }
   }, [id])

   const handleLeadSelect = (transformedLead: Lead) => {
      const leadId = transformedLead.leadId
      const originalLead = filteredLeads?.find((lead: DemoLead) => lead.leadId === leadId)

      setSelectedLeadId(leadId)
      onSelectLead(leadId, originalLead)

      const storageKey = getStorageKey(selectedStageFilter, 'Demo Source')
      localStorage.setItem(storageKey, leadId.toString())

      const currentUrl = new URL(window.location.href)
      const searchParams = currentUrl.searchParams.toString()
      const urlSuffix = searchParams ? `?${searchParams}` : ''

      navigate(`/demo-conversations/${leadId}${urlSuffix}`)
   }

   // Strip HTML from content
   const stripHtml = (html: string): string => {
      const tmp = document.createElement('div')
      tmp.innerHTML = html
      return tmp.textContent || tmp.innerText || ''
   }

   if (loading) {
      return (
         <div className="flex flex-col w-full lg:w-[400px] bg-white/5 rounded-lg p-4">
            <div className="flex items-center justify-center h-32">
               <div className="text-white/60">Loading demo leads...</div>
            </div>
         </div>
      )
   }

   return (
      <div className="flex flex-col w-full lg:max-w-[340px] 2xl:max-w-[420px] max-h-[400px] lg:max-h-full">
         {/* Search Field */}
         <SearchField
            className="flex py-4 mb-4 border rounded-lg shrink-0 border-primary/40 bg-primary/10"
            placeholder="Search Contacts"
            value={searchInput}
            onChange={handleSearchChange}
         />

         {/* Lead List Container */}
         <div className="flex-grow overflow-y-auto border rounded-lg md:w-full bg-primary/10 border-white/10">
            {/* Header */}
            <div className="flex px-8 pt-5 pb-6 font-medium text-white/70">
               Recent Conversations
            </div>

            {/* Lead List */}
            <div className="flex flex-col gap-[2px] px-3 pb-4">
               {filteredLeads.length === 0 && !loading ? (
                  <div className="flex items-center justify-center h-32 text-white/60">
                     {debouncedSearchTerm.trim()
                        ? `No leads found matching "${debouncedSearchTerm}"`
                        : "No leads found for selected stage"
                     }
                  </div>
               ) : (
                  filteredLeads.map((lead: DemoLead) => {
                     const transformedLead = transformDummyLead(lead)
                     const leadConversationData = getLeadConversationData(lead.leadId)
                     return (
                        <LeadListItem
                           key={`${lead.leadId}-${leadConversationData?.lastUpdate || 'static'}`}
                           lead={transformedLead}
                           isSelected={selectedLeadId === lead.leadId}
                           onSelect={handleLeadSelect}
                           stripHtml={stripHtml}
                        />
                     )
                  })
               )}
            </div>
         </div>
      </div>
   )
}